import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../components/Header";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import { uploadDocument } from "../../../data/Axios/DynamicService";
import { companyManagementUrl } from "../../../config";

const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024; // 1MB
const MAX_FILE_SIZE_LABEL = "1MB";
const SUPPORTED_FORMATS = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/svg+xml",
];
const ACCEPTED_FILE_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const validationSchema = yup.object().shape({
  file: yup
    .mixed()
    .required("Please select an image to upload!")
    .test(
      "fileSize",
      `The image exceeds the maximum allowed size of ${MAX_FILE_SIZE_LABEL}.`,
      (value) => {
        return value && value.size <= MAX_FILE_SIZE_BYTES;
      }
    )
    .test(
      "fileType",
      "Unsupported file format. Please upload a PNG, JPG, JPEG, WEBP, or SVG image.",
      (value) => {
        return value && SUPPORTED_FORMATS.includes(value.type);
      }
    ),
});

const LogoUploadForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const base_url = companyManagementUrl.upload_logo_uri;

  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const initialValues = {
    file: null,
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const logoObject = {
        logoUpload: true,
      };

      const response = await uploadDocument(
        props.isEditing ? "remove" : "add",
        values.file,
        logoObject,
        base_url
      );

      if (response && response.status === 200) {
        // Read file as Data URL for instant local fallback in case remote server returns 404
        if (values.file) {
          const reader = new FileReader();
          reader.onload = () => {
            if (reader.result) {
              localStorage.setItem("uploadedLogoDataUrl", reader.result);
              window.dispatchEvent(new Event("logo-updated"));
            }
          };
          reader.readAsDataURL(values.file);
        }

        // Extract the new logo path from the response
        const newLogoUrl = response.data?.LogoUrl || response.data?.logoUrl;
        if (newLogoUrl) {
          // Build the full URL using the same API base the login uses
          const apiBase = companyManagementUrl.upload_logo_uri.replace(
            /\/company\/uploadlogo.*/i,
            ""
          );
          // Ensure leading slash so path is consistent with login-stored format
          const normalizedPath = newLogoUrl.startsWith("/")
            ? newLogoUrl
            : `/${newLogoUrl}`;
          const fullLogoPath = `${apiBase}${normalizedPath}`;
          // Update localStorage so future reloads/logins see the new logo
          localStorage.setItem("filePath", fullLogoPath);
          // Notify the Sidebar to update immediately (no page reload needed)
          window.dispatchEvent(new Event("logo-updated"));
        }
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Company logo uploaded successfully.",
        });
        resetForm();
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }

      setSubmitting(false);
    } catch (error) {
      console.error("Upload logo error:", error);
      const is413 =
        error?.response?.status === 413 ||
        error?.status === 413 ||
        error?.message?.includes("413");

      if (is413) {
        Swal.fire({
          icon: "error",
          title: "Image Too Large!",
          html: `The server rejected the upload because the file exceeds the maximum allowed size (<strong>413 Request Entity Too Large</strong>).<br/><br/>The required maximum size is <strong>${MAX_FILE_SIZE_LABEL}</strong>.<br/>Please compress or select an image smaller than ${MAX_FILE_SIZE_LABEL} and try again.`,
        });
      } else {
        const errorMsg =
          error?.response?.data?.message ||
          (typeof error?.response?.data === "string" ? error.response.data : null) ||
          error?.message ||
          "An error occurred while processing your request. Please try again later.";
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: errorMsg,
        });
      }
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Header
        title="Company Logo"
        subtitle={`Select an image to upload (Required size: Max ${MAX_FILE_SIZE_LABEL} • PNG, JPG, JPEG, SVG)`}
      />

      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: "8px",
          bgcolor: colors.primary[400],
          maxWidth: "800px",
          mt: 2,
        }}
      >
        {/* Requirement info banner */}
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          p={1.5}
          mb={3}
          sx={{
            borderRadius: "6px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)",
            border: `1px solid ${colors.grey[700] || "rgba(0,0,0,0.1)"}`,
          }}
        >
          <InfoOutlinedIcon color="info" fontSize="small" />
          <Typography variant="body2" color={colors.grey[100]}>
            <strong>Required size:</strong> Maximum <strong>{MAX_FILE_SIZE_LABEL}</strong> (1,024 KB) • <strong>Formats:</strong> PNG, JPG, JPEG, WEBP, SVG
          </Typography>
        </Box>

        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => {
            const isFileSelected = !!values.file;
            const isTooLarge =
              isFileSelected && values.file.size > MAX_FILE_SIZE_BYTES;
            const isUnsupportedFormat =
              isFileSelected && !SUPPORTED_FORMATS.includes(values.file.type);
            const hasError = isTooLarge || isUnsupportedFormat;

            const handleFileChange = (event) => {
              const file =
                event.currentTarget.files && event.currentTarget.files[0];
              if (file) {
                setFieldValue("file", file);
                setFieldTouched("file", true, true);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setPreviewUrl(URL.createObjectURL(file));
              }
              // Reset file input value so selecting the same file triggers onChange
              event.currentTarget.value = "";
            };

            const handleRemoveFile = () => {
              setFieldValue("file", null);
              setFieldTouched("file", false, false);
              if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
              }
            };

            return (
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                {/* File picker button */}
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      bgcolor: colors.blueAccent?.[700] || colors.grey[400],
                      color: "#fff",
                      px: 3,
                      py: 1.2,
                      fontWeight: "bold",
                      textTransform: "none",
                      fontSize: "0.95rem",
                      "&:hover": {
                        bgcolor: colors.blueAccent?.[800] || colors.grey[500],
                      },
                    }}
                  >
                    {isFileSelected
                      ? "Change Selected Image"
                      : "Select an image to upload"}
                    <input
                      id="file"
                      name="file"
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>

                  {isFileSelected && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleRemoveFile}
                      sx={{ textTransform: "none" }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>

                {/* Validation error for empty file if touched */}
                {touched.file && errors.file && !isFileSelected && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.file}
                  </Typography>
                )}

                {/* Selected File Details & Alerts */}
                {isFileSelected && (
                  <Box mt={3}>
                    {/* Error Alert: File too large */}
                    {isTooLarge && (
                      <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon fontSize="inherit" />}
                        sx={{ mb: 2 }}
                      >
                        <AlertTitle sx={{ fontWeight: "bold" }}>
                          Image is too large ({formatBytes(values.file.size)})
                        </AlertTitle>
                        The selected file exceeds the required maximum size of{" "}
                        <strong>{MAX_FILE_SIZE_LABEL}</strong> (1,024 KB).
                        Please choose an image smaller than {MAX_FILE_SIZE_LABEL} to upload.
                      </Alert>
                    )}

                    {/* Error Alert: Unsupported format */}
                    {isUnsupportedFormat && (
                      <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon fontSize="inherit" />}
                        sx={{ mb: 2 }}
                      >
                        <AlertTitle sx={{ fontWeight: "bold" }}>
                          Unsupported File Format
                        </AlertTitle>
                        Please upload an image file in PNG, JPG, JPEG, WEBP, or SVG format.
                      </Alert>
                    )}

                    {/* Success Alert: File is within limit */}
                    {!hasError && (
                      <Alert
                        severity="success"
                        icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                        sx={{ mb: 2 }}
                      >
                        Image ready to upload:{" "}
                        <strong>{values.file.name}</strong> (
                        {formatBytes(values.file.size)}) — within required size limit.
                      </Alert>
                    )}

                    {/* Preview card */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={3}
                      p={2}
                      sx={{
                        borderRadius: "8px",
                        border: `1px dashed ${
                          isTooLarge
                            ? theme.palette.error.main
                            : colors.grey[600]
                        }`,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                        maxWidth: "500px",
                      }}
                    >
                      {previewUrl && (
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Logo Preview"
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "contain",
                            borderRadius: "6px",
                            border: `1px solid ${colors.grey[700]}`,
                            bgcolor: "#fff",
                            p: 0.5,
                          }}
                        />
                      )}
                      <Box flex={1}>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          noWrap
                          title={values.file.name}
                        >
                          {values.file.name}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          mt={1}
                          alignItems="center"
                        >
                          <Chip
                            label={`Size: ${formatBytes(values.file.size)}`}
                            size="small"
                            color={isTooLarge ? "error" : "success"}
                            variant="outlined"
                          />
                          <Chip
                            label={
                              isTooLarge
                                ? `Exceeds ${MAX_FILE_SIZE_LABEL} limit`
                                : `Max: ${MAX_FILE_SIZE_LABEL}`
                            }
                            size="small"
                            color={isTooLarge ? "error" : "default"}
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* CTA Action Area */}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  mt={4}
                  gap={1}
                >
                  {isTooLarge && (
                    <Typography
                      variant="body2"
                      color="error"
                      fontWeight="600"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <ErrorOutlineIcon fontSize="small" />
                      Image is too large ({formatBytes(values.file.size)}). Required size is {MAX_FILE_SIZE_LABEL} or less.
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting || !isFileSelected || hasError}
                    sx={{
                      minWidth: "160px",
                      py: 1,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                      textTransform: "none",
                    }}
                  >
                    {isSubmitting
                      ? "Uploading..."
                      : isTooLarge
                      ? `Image Too Large (Max ${MAX_FILE_SIZE_LABEL})`
                      : "Submit"}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default LogoUploadForm;
